import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';
import { useLanguage } from '../../../contexts/LanguageContext';
import ConsultationModal from '../../../components/common/ConsultationModal';
import HeroNetworkBackground from '../../../components/HeroNetworkBackground';
import AnimatedText from '../../../components/AnimatedText';

const HeroSection = () => {
  const navigate = useNavigate();
  const { t, tm } = useLanguage();
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isMounted, setIsMounted] = useState(false);
  const [isConsultationModalOpen, setIsConsultationModalOpen] = useState(false);

  const slideCopy = tm('home.hero.slides');

  const heroSlides = useMemo(
    () => [
      {
        id: 1,
        ...slideCopy[0],
        image: '/assets/images/precision_partnership_banner.png',
        ctaAction: () => navigate('/service-portfolio-hub'),
        statsValue: '880+',
      },
      {
        id: 2,
        ...slideCopy[1],
        image: '/assets/images/global_business_expertise_banner.png',
        ctaAction: () => navigate('/job-marketplace'),
        statsValue: '678+',
      },
      {
        id: 3,
        ...slideCopy[2],
        image: '/assets/images/innovation_success_banner.png',
        ctaAction: () => navigate('/success-stories-showcase'),
        statsValue: '112',
      },
    ],
    [slideCopy, navigate]
  );

  useEffect(() => {
    setIsMounted(true);
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % heroSlides.length);
    }, 6000);

    return () => clearInterval(timer);
  }, [heroSlides.length]);

  const currentHero = heroSlides[currentSlide];

  return (
    <>
      <section className="relative bg-[#060D21] bg-[radial-gradient(ellipse_80%_80%_at_50%_-20%,rgba(30,58,138,0.35),rgba(6,13,33,1))] overflow-hidden border-b border-slate-800/80">
        
        {/* Animated Gold Circuit Network Background */}
        <HeroNetworkBackground />

        {/* Circuit-Gear Logo Motif Watermark & Ambient Glows */}
        <div className="absolute inset-0 pointer-events-none overflow-hidden select-none z-0">
          <div className="absolute -top-40 -left-40 w-[500px] h-[500px] bg-amber-500/10 rounded-full blur-[140px]" />
          <div className="absolute top-1/2 -right-40 w-[600px] h-[600px] bg-blue-600/15 rounded-full blur-[160px]" />
          <div className="absolute inset-0 bg-[linear-gradient(to_right,#1e293b18_1px,transparent_1px),linear-gradient(to_bottom,#1e293b18_1px,transparent_1px)] bg-[size:4rem_4rem] [mask-image:radial-gradient(ellipse_60%_50%_at_50%_0%,#000_70%,transparent_100%)]" />

          <svg
            className="absolute right-0 top-1/2 -translate-y-1/2 w-[650px] h-[650px] text-amber-400 opacity-[0.07]"
            viewBox="0 0 500 500"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5"
          >
            <circle cx="250" cy="250" r="180" strokeDasharray="8 8" />
            <circle cx="250" cy="250" r="140" strokeWidth="2" />
            <circle cx="250" cy="250" r="90" strokeWidth="1" />
            <circle cx="250" cy="250" r="30" fill="currentColor" fillOpacity="0.2" />
            {[0, 30, 60, 90, 120, 150, 180, 210, 240, 270, 300, 330].map((angle, i) => (
              <line
                key={i}
                x1="250"
                y1="250"
                x2={250 + 195 * Math.cos((angle * Math.PI) / 180)}
                y2={250 + 195 * Math.sin((angle * Math.PI) / 180)}
                strokeWidth="4"
                strokeDasharray="15 165"
              />
            ))}
            <path d="M 250 110 L 250 30 L 320 30" strokeWidth="2" />
            <circle cx="320" cy="30" r="5" fill="currentColor" />
            <path d="M 390 250 L 470 250 L 470 190" strokeWidth="2" />
            <circle cx="470" cy="190" r="5" fill="currentColor" />
            <path d="M 250 390 L 250 470 L 170 470" strokeWidth="2" />
            <circle cx="170" cy="470" r="5" fill="currentColor" />
            <path d="M 110 250 L 30 250 L 30 310" strokeWidth="2" />
            <circle cx="30" cy="310" r="5" fill="currentColor" />
          </svg>
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 md:px-6 lg:px-8 py-16 md:py-24 lg:py-28">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
            
            {/* Left Column: Staggered Entrance Content */}
            <div className="lg:col-span-7 space-y-6 md:space-y-8">
              
              {/* 1. ISO 9001:2015 Badge Pill (delay 0ms) */}
              <div
                style={{ transitionDelay: '0ms' }}
                className={`inline-flex items-center gap-2.5 px-3.5 py-1.5 rounded-full bg-white/[0.06] backdrop-blur-md border border-amber-500/20 text-slate-200 text-xs md:text-sm font-medium shadow-sm transition-all duration-700 ease-out transform ${
                  isMounted ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
                }`}
              >
                <Icon name="Award" size={15} className="text-amber-400 flex-shrink-0" />
                <span>{t('home.hero.isoBadge')}</span>
              </div>

              {/* Headline & Tagline (delay 100ms & 200ms) */}
              <div className="space-y-3">
                <h1
                  style={{ transitionDelay: '100ms' }}
                  className={`text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-extrabold text-white tracking-tight leading-[1.12] transition-all duration-700 ease-out transform ${
                    isMounted ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
                  }`}
                >
                  A Leading Automation Consulting Company
                </h1>
                
                <p
                  style={{ transitionDelay: '200ms' }}
                  className={`text-lg md:text-xl lg:text-2xl font-semibold text-amber-400 tracking-wide font-sans transition-all duration-700 ease-out transform ${
                    isMounted ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
                  }`}
                >
                  Precision Solutions for a Connected World
                </p>
              </div>

              {/* 4. Paragraph Description */}
              <AnimatedText
                text={currentHero?.description}
                as="p"
                delay={300}
                className="text-sm md:text-base lg:text-lg text-slate-300/90 leading-relaxed max-w-xl font-normal"
              />

              {/* 5. CTA Buttons (delay 400ms) */}
              <div
                style={{ transitionDelay: '400ms' }}
                className={`flex flex-col sm:flex-row gap-4 pt-2 transition-all duration-700 ease-out transform ${
                  isMounted ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
                }`}
              >
                <Button
                  variant="default"
                  size="lg"
                  onClick={currentHero?.ctaAction}
                  className="px-7 py-3.5 rounded-xl bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-400 hover:to-amber-500 text-slate-950 font-extrabold text-sm md:text-base transition-all duration-300 shadow-lg shadow-amber-500/20 hover:shadow-amber-500/35 hover:-translate-y-0.5 border-0"
                  iconName="ArrowRight"
                  iconPosition="right"
                >
                  {currentHero?.cta}
                </Button>
                <Button
                  variant="outline"
                  size="lg"
                  onClick={() => setIsConsultationModalOpen(true)}
                  className="px-7 py-3.5 rounded-xl bg-white/[0.05] hover:bg-white/[0.12] text-white font-semibold text-sm md:text-base border border-white/20 hover:border-amber-400/40 backdrop-blur-md transition-all duration-300 hover:-translate-y-0.5"
                  iconName="Phone"
                  iconPosition="left"
                >
                  {t('home.hero.scheduleConsultation')}
                </Button>
              </div>

              {/* 6. Stats Carousel Row (delay 500ms) */}
              <div
                style={{ transitionDelay: '500ms' }}
                className={`pt-6 border-t border-slate-800/80 flex flex-wrap items-center justify-between gap-6 transition-all duration-700 ease-out transform ${
                  isMounted ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
                }`}
              >
                <div className="flex items-baseline gap-3">
                  <div className="text-3xl md:text-4xl lg:text-5xl font-black text-amber-400 tracking-tight transition-all duration-300">
                    {currentHero?.statsValue}
                  </div>
                  <div className="text-xs md:text-sm text-slate-300 font-medium max-w-[200px]">
                    {currentHero?.statsLabel}
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  {heroSlides.map((_, index) => (
                    <button
                      key={index}
                      onClick={() => setCurrentSlide(index)}
                      className={`h-2 rounded-full transition-all duration-300 ${
                        index === currentSlide
                          ? 'bg-amber-400 w-8 shadow-sm shadow-amber-400/50'
                          : 'bg-white/25 hover:bg-white/50 w-2'
                      }`}
                      aria-label={t('common.goToSlide', { n: index + 1 })}
                    />
                  ))}
                </div>
              </div>
            </div>

            {/* Right Column: Frame Photo & Floating Card (delay 600ms) */}
            <div
              style={{ transitionDelay: '600ms' }}
              className={`lg:col-span-5 relative mt-6 lg:mt-0 transition-all duration-700 ease-out transform ${
                isMounted ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
              }`}
            >
              <div className="relative rounded-2xl md:rounded-3xl p-2 md:p-3 bg-gradient-to-b from-white/15 via-white/5 to-transparent border border-white/15 shadow-2xl backdrop-blur-xl">
                <div className="rounded-xl md:rounded-2xl overflow-hidden aspect-[4/3] lg:aspect-[5/4] w-full relative">
                  <Image
                    src={currentHero?.image}
                    alt={currentHero?.imageAlt}
                    className="w-full h-full object-cover block transition-all duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-slate-950/40 via-transparent to-transparent" />
                </div>
              </div>

              <div className="absolute -bottom-6 -right-3 md:-bottom-8 md:-right-6 bg-slate-900/90 backdrop-blur-xl border border-slate-700/80 rounded-2xl shadow-2xl p-4 md:p-5 max-w-[240px] md:max-w-[260px] text-white transition-all duration-300 hover:scale-105">
                <div className="flex items-center gap-3.5">
                  <div className="w-11 h-11 md:w-13 md:h-13 rounded-xl bg-amber-500/15 border border-amber-500/25 text-amber-400 flex items-center justify-center flex-shrink-0 shadow-inner">
                    <Icon name="TrendingUp" size={22} className="text-amber-400" />
                  </div>
                  <div>
                    <div className="text-xl md:text-2xl font-black text-amber-400 tracking-tight">98.5%</div>
                    <div className="text-xs text-slate-300 font-medium leading-snug">
                      {t('home.hero.clientSatisfaction')}
                    </div>
                  </div>
                </div>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* Consultation Modal Window */}
      <ConsultationModal
        isOpen={isConsultationModalOpen}
        onClose={() => setIsConsultationModalOpen(false)}
      />
    </>
  );
};

export default HeroSection;
